import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Activity,
  Users,
  Calendar,
  Heart,
  Dumbbell,
  ShieldCheck,
  MapPin,
  FileText,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Shield,
  Zap,
  Wind,
  ClipboardList,
  TrendingUp,
  LayoutDashboard,
  Bell,
  Crown,
  Apple,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const StaffLayout = ({ children }) => {
  const { user, logout, isAdmin, api } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(localStorage.getItem('fb_team_context') || 'all');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await api.get('/teams');
        setTeams(response.data);
      } catch (error) {
        console.error('Failed to fetch teams:', error);
      }
    };
    fetchTeams();
  }, [api]);

  useEffect(() => {
    localStorage.setItem('fb_team_context', selectedTeam);
  }, [selectedTeam]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { 
      section: 'dashboard',
      label: language === 'IT' ? 'Dashboard' : 'Dashboard',
      icon: LayoutDashboard, 
      href: '/staff/dashboard',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH', 'NUTRITIONIST'],
    },
    { 
      section: 'roster',
      label: t('roster'), 
      icon: Users, 
      href: '/staff/roster',
      children: [
        { label: t('athletes'), href: '/staff/roster' },
        { label: t('teams'), href: '/staff/roster/teams' },
      ]
    },
    { 
      section: 'nutrition',
      label: language === 'IT' ? 'Nutrizione' : 'Nutrition',
      icon: Apple,
      href: '/staff/nutrition',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH', 'NUTRITIONIST'],
    },
    { 
      section: 'planner',
      label: t('planner'), 
      icon: Calendar, 
      href: '/staff/planner',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH', 'NUTRITIONIST'],
    },
    { 
      section: 'wellness',
      label: t('wellness'), 
      icon: Heart, 
      href: '/staff/wellness',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH'],
    },
    { 
      section: 'vbt',
      label: t('vbt'), 
      icon: Dumbbell, 
      href: '/staff/vbt',
      roles: ['ADMIN', 'SNC'],
    },
    { 
      section: 'jump',
      label: language === 'IT' ? 'Test Salto' : 'Jump Testing',
      icon: Zap, 
      href: '/staff/jump',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH'],
    },
    { 
      section: 'aerobic',
      label: language === 'IT' ? 'Test Aerobici' : 'Aerobic Testing',
      icon: Wind, 
      href: '/staff/aerobic',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH'],
    },
    { 
      section: 'rtp',
      label: t('rtp'), 
      icon: ShieldCheck, 
      href: '/staff/rtp',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH'],
    },
    { 
      section: 'gps',
      label: t('gps'), 
      icon: MapPin, 
      href: '/staff/gps',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH'],
    },
    { 
      section: 'reports',
      label: t('reports'), 
      icon: FileText, 
      href: '/staff/reports',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH'],
    },
    { 
      section: 'trends',
      label: language === 'IT' ? 'Trend' : 'Trends',
      icon: TrendingUp, 
      href: '/staff/trends',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH'],
    },
    { 
      section: 'prescriptions',
      label: language === 'IT' ? 'Prescrizioni' : 'Prescriptions',
      icon: ClipboardList, 
      href: '/staff/prescriptions',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH'],
    },
    { 
      section: 'notifications',
      label: language === 'IT' ? 'Notifiche' : 'Notifications',
      icon: Bell, 
      href: '/staff/notifications',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH'],
    },
    { 
      section: 'export',
      label: language === 'IT' ? 'Esporta Dati' : 'Data Export',
      icon: FileText, 
      href: '/staff/export',
      roles: ['ADMIN', 'SNC', 'PHYSIO', 'COACH'],
    },
    { 
      section: 'executive',
      label: language === 'IT' ? 'Suite Executive' : 'Executive Suite',
      icon: Crown, 
      href: '/staff/executive/season',
      roles: ['ADMIN', 'SUPERADMIN', 'SNC', 'COACH'],
    },
  ];

  const adminItems = [
    { 
      section: 'admin',
      label: t('admin'), 
      icon: Shield, 
      href: '/staff/admin',
      roles: ['ADMIN'],
    },
    { 
      section: 'season',
      label: language === 'IT' ? 'Transizione Stagione' : 'Season Transition',
      icon: Calendar, 
      href: '/staff/admin/season',
      roles: ['ADMIN'],
    },
    { 
      section: 'users',
      label: language === 'IT' ? 'Gestione Utenti' : 'User Management',
      icon: Users, 
      href: '/staff/admin/users',
      roles: ['ADMIN'],
    },
  ];

  const isActive = (href) => location.pathname.startsWith(href);

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    const hasAccess = !item.roles || item.roles.includes(user?.role);

    if (!hasAccess) return null;

    if (item.disabled) {
      return (
        <div className="px-3 py-2 flex items-center gap-3 text-zinc-600 cursor-not-allowed">
          <Icon className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-sm font-medium">{item.label}</span>
          <span className="ml-auto text-xs bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-500">Soon</span>
        </div>
      );
    }

    if (item.children) {
      return (
        <div className="space-y-1">
          <div className={`px-3 py-2 flex items-center gap-3 ${active ? 'text-zinc-100' : 'text-zinc-400'}`}>
            <Icon className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-sm font-medium">{item.label}</span>
          </div>
          <div className="ml-8 space-y-1">
            {item.children.map((child) => (
              <Link
                key={child.href}
                to={child.href}
                className={`block px-3 py-1.5 text-sm rounded-sm transition-colors ${
                  location.pathname === child.href
                    ? 'bg-zinc-800/50 text-amber-500 border-l-2 border-amber-500 -ml-0.5 pl-[14px]'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                {child.label}
              </Link>
            ))}
          </div>
        </div>
      );
    }

    return (
      <Link
        to={item.href}
        className={`px-3 py-2 flex items-center gap-3 rounded-sm transition-colors ${
          active
            ? 'bg-zinc-800/50 text-amber-500 border-l-2 border-amber-500'
            : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/30'
        }`}
        onClick={() => setSidebarOpen(false)}
      >
        <Icon className="w-5 h-5" strokeWidth={1.5} />
        <span className="text-sm font-medium">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Mobile header — safe-area aware (notch / Dynamic Island) */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        <div className="flex items-center justify-between px-4 h-14">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 -ml-2 text-zinc-400 hover:text-zinc-100"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2.5">
            <img src="/logo/fb-symbol-mobile.png" alt="FB Performance" className="w-7 h-7 object-contain" />
            <div className="leading-none">
              <span className="font-semibold text-sm text-zinc-100 block" data-testid="mobile-org-name">{user?.organization_name || 'FB Performance'}</span>
            </div>
          </div>
          <button
            onClick={toggleLanguage}
            className="px-2 py-1 text-xs font-semibold tracking-wider uppercase text-zinc-400 hover:text-zinc-100"
          >
            {language}
          </button>
        </div>
      </div>

      {/* Sidebar — safe-area aware on mobile */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-zinc-950 border-r border-zinc-800 transform transition-transform duration-200 lg:translate-x-0 flex flex-col overflow-hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
        }}
      >
        {/* Brand header */}
        <div className="py-5 px-5 shrink-0">
          <img src="/logo/fb-logo-dark.png" alt="FB Performance" className="h-8 lg:h-9 w-auto object-contain" data-testid="sidebar-logo" />
          {user?.organization_name && (
            <div className="mt-2 text-xs font-medium text-zinc-400 truncate" data-testid="sidebar-org-name">{user.organization_name}</div>
          )}
          <div className="mt-3 h-px bg-gradient-to-r from-zinc-700/60 via-zinc-800/40 to-transparent" />
        </div>

        {/* Team selector */}
        <div className="p-4 border-b border-zinc-800 shrink-0">
          <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-2 block">
            {t('team')}
          </label>
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm" data-testid="team-selector">
              <SelectValue placeholder="Select team" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all" className="text-zinc-100">{t('all')} {t('teams')}</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id} className="text-zinc-100">
                  {team.code} - {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Navigation — flex-1 + overflow-y-auto fills remaining space between header and footer */}
        <nav className="flex-1 min-h-0 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavItem key={item.section} item={item} />
          ))}
          
          {isAdmin && (
            <>
              <div className="my-3 border-t border-zinc-800" />
              {adminItems.map((item) => (
                <NavItem key={item.section} item={item} />
              ))}
            </>
          )}
        </nav>

        {/* User menu — shrink-0 stays pinned at bottom */}
        <div className="shrink-0 p-4 border-t border-zinc-800 bg-zinc-950">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-zinc-800/50 transition-colors" data-testid="user-menu">
                <div className="w-8 h-8 bg-zinc-800 rounded-sm flex items-center justify-center text-zinc-400 text-sm font-semibold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-zinc-100">{user?.first_name} {user?.last_name}</div>
                  <div className="text-xs text-zinc-500">{user?.role}</div>
                </div>
                <ChevronDown className="w-4 h-4 text-zinc-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-zinc-900 border-zinc-800" align="end">
              <DropdownMenuItem onClick={() => navigate('/settings')} className="text-zinc-400 focus:text-zinc-100 focus:bg-zinc-800" data-testid="settings-link">
                <Settings className="w-4 h-4 mr-2" />
                {t('settings')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleLanguage} className="text-zinc-400 focus:text-zinc-100 focus:bg-zinc-800">
                <span className="w-4 h-4 mr-2 text-center font-mono text-xs">{language}</span>
                Language / Lingua
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem onClick={handleLogout} className="text-rose-400 focus:text-rose-300 focus:bg-rose-500/10" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:pl-64 pt-[calc(env(safe-area-inset-top)+3.5rem)] lg:pt-0">
        <div className="hidden lg:flex h-14 items-center justify-end gap-4 px-6 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-20">
          <button
            onClick={toggleLanguage}
            className="px-3 py-1.5 text-xs font-semibold tracking-wider uppercase bg-zinc-900 border border-zinc-800 rounded-sm text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
            data-testid="desktop-language-toggle"
          >
            {language === 'EN' ? 'IT' : 'EN'}
          </button>
        </div>
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default StaffLayout;
